'use client'

import { useEffect } from 'react'
import Link from 'next/link'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { AlertTriangle, RefreshCw, BookOpen } from 'lucide-react'

// Catches uncaught errors during article render so a transient Directus
// hiccup during ISR regeneration doesn't get cached as a static 500.
export default function BlogPostError({
  error,
  reset,
}: {
  error: Error & { digest?: string }
  reset: () => void
}) {
  useEffect(() => {
    console.error('Blog post error:', error)
  }, [error])

  return (
    <div className="min-h-screen bg-linear-to-br from-green-50 to-emerald-50 flex items-center justify-center p-4">
      <Card className="max-w-lg w-full">
        <CardHeader>
          <CardTitle className="flex items-center gap-3 text-red-600">
            <AlertTriangle className="w-6 h-6" />
            This article is having trouble loading
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-gray-600">
            We hit a snag fetching this post. Try again in a moment, or browse other articles in the meantime.
          </p>

          {process.env.NODE_ENV === 'development' && (
            <div className="bg-red-50 border border-red-200 rounded p-3">
              <p className="text-sm font-mono text-red-800 break-all">
                {error.message}
              </p>
            </div>
          )}

          <div className="flex gap-3 pt-4">
            <Button
              onClick={reset}
              className="flex-1 flex items-center gap-2"
            >
              <RefreshCw className="w-4 h-4" />
              Try Again
            </Button>
            <Button asChild variant="outline" className="flex-1 flex items-center gap-2">
              <Link href="/blog">
                <BookOpen className="w-4 h-4" />
                Browse Articles
              </Link>
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
